import type {
  AcUnits,
  GeneratorFuel,
  HeatingFuel,
  LargeLoad,
  TierId,
} from "@ks-gen/shared";

/** A customer-uploaded file as it moves through the funnel (bill, panel photo, …). */
export interface Attachment {
  file: File | null;
  gcsUri: string | null;
  objectPath: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
}

const emptyAttachment = (): Attachment => ({
  file: null,
  gcsUri: null,
  objectPath: null,
  mimeType: null,
  sizeBytes: null,
});

/** In-progress form state. Mirrors the n8n field semantics exactly. */
export interface FormState {
  submissionId: string;
  // Step 1
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  contactConsent: boolean;
  // Step 2
  sqft: number | null;
  heatingFuel: HeatingFuel | null;
  acUnits: AcUnits;
  /** NEC 220.82 inputs. coolingTons drives sizing; acUnits kept for the API contract. */
  coolingTons: number;
  wellPump: boolean;
  largeLoads: LargeLoad[];
  /** Fuel & site inputs for fuel-derating + 4-lane triage. */
  generatorFuel: GeneratorFuel;
  /** null = customer doesn't know (gates whole-home to an assessment). */
  serviceAmps: number | null;
  gasMainAvailable: boolean;
  willingToSetPropaneTank: boolean;
  // Step 3
  bill: Attachment;
  /** Optional electrical-panel photo (issue #27). Same upload path as the bill. */
  panelPhoto: Attachment;
  // Step 4
  selectedTier: TierId | null;
  /** adder id -> quantity (0 or absent = not selected) */
  selectedAdders: Record<string, number>;
  /** recurring Member-club opt-in */
  membershipOptIn: boolean;
  // consent (captured at step 4->5)
  esignConsent: boolean;
  /** ISO timestamp of the affirmative e-sign consent action; null until checked. */
  esignConsentAt: string | null;
}

export function createSubmissionId(): string {
  // Browser-native UUIDv4. Generated at form start; carried through every system.
  return crypto.randomUUID();
}

export function initialState(): FormState {
  return {
    submissionId: createSubmissionId(),
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    street: "",
    city: "",
    state: "NY",
    zip: "",
    contactConsent: false,
    // Safe pre-fills so a user reaches a recommendation fast, then refines.
    // heatingFuel + serviceAmps stay unset so we never firm-quote an unconfirmed service.
    sqft: 2000,
    heatingFuel: null,
    acUnits: 1,
    coolingTons: 3,
    wellPump: false,
    largeLoads: [],
    generatorFuel: "propane",
    serviceAmps: null,
    gasMainAvailable: false,
    willingToSetPropaneTank: true,
    bill: emptyAttachment(),
    panelPhoto: emptyAttachment(),
    selectedTier: null,
    selectedAdders: {},
    membershipOptIn: false,
    esignConsent: false,
    esignConsentAt: null,
  };
}
